/* eslint-disable @typescript-eslint/no-floating-promises */

import { useState, useEffect, useCallback } from 'react';
import PortalIdParser from 'PortalIdParser';
import getIn from 'transmute/getIn';
import { getExternalFromNumbers, getPortalFromNumbers, getConnectFromNumbers, getStandaloneConnectionId, getTwilioDeviceStatus } from 'calling-cross-tab-library/appData/operators/widgetDetailsOperators';
import { sendCrossTabMessage } from 'calling-cross-tab-library/external/sendCrossTabMessage';
import { createInitiateOutboundCallMessage } from 'calling-lifecycle-messages/deviceEventCreators';
import { getAccountTypeFromPhoneNumber } from 'calling-ui-library/utils/getAccountTypeFromPhoneNumber';
import { callingWindowUrl } from 'calling-lifecycle-internal/utils/urlUtils';
import { getPropertyName, getObjectId, getObjectTypeId, getPhoneNumberProperties, getMetadata } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
// @ts-expect-error not typed
import InboundCallingTracker from 'calling-lifecycle-usage-tracker/inboundCallingTracker';
import { useWidgetDetails } from 'calling-cross-tab-library/external/useWidgetDetails';
import { getIsTwilioBasedCallProvider } from 'calling-lifecycle-internal/call-provider/operators/getIsTwilioBasedCallProvider';
import { logCallingError } from 'calling-error-reporting/report/error';
import { CallSource } from 'calling-cross-tab-library/constants/CallSource';
import { CommunicatorLogger } from 'calling-lifecycle-usage-tracker/callingTracker';
import { ACTIVITY_CREATOR } from 'calling-internal-common/constants/appIdentifiers';
import { PhoneNumberSources } from 'calling-internal-common/constants/PhoneNumberSources';
import { triggerThirdPartyOutboundCall } from '../utils/triggerThirdPartyOutboundCall';
import { getIsThirdPartyCallingPopoverInUse } from 'calling-internal-common/utils/isThirdPartyCallingPopoverInUse';
import { CALL_FROM_BROWSER } from 'calling-internal-common/constants/CallMethods';
import { setPersistedCallMethod } from 'calling-lifecycle-internal/utils/getLocalCallSettings';
import { isDeviceReady } from 'calling-client-interface/utils/deviceStatusHelpers';
import { CALL_WINDOW_HEIGHT_WITHOUT_PX, CALL_WINDOW_WIDTH_WITHOUT_PX } from '../constants/BaseLayoutDimensions';
export const useHandleOpenWindowAndStartCall = props => {
  const {
    onCallButtonClick,
    defaultFromNumber,
    prospectingQueueItemId,
    selectedCallProvider,
    onLegacyOutboundCallStart,
    appIdentifier,
    gates = [],
    threadId,
    source,
    ticketId,
    requireContactOrCompany
  } = props;
  const {
    widgetDetails
  } = useWidgetDetails();
  const activeConnectionId = getStandaloneConnectionId(widgetDetails);
  const activeCallTabOpen = Boolean(activeConnectionId);
  const twilioDeviceStatus = getTwilioDeviceStatus(widgetDetails);
  const portalFromNumbers = getPortalFromNumbers(widgetDetails) || [];
  const externalFromNumbers = getExternalFromNumbers(widgetDetails) || [];
  const connectFromNumbers = getConnectFromNumbers(widgetDetails) || [];
  const fromNumbers = [...portalFromNumbers, ...externalFromNumbers, ...connectFromNumbers];
  const [selectedFromNumber, setSelectedFromNumber] = useState(defaultFromNumber);
  const [isTryingToStartCall, setIsTryingToStartCall] = useState(false);
  const [_numberIdentifier, setNumberIdentifier] = useState(null);
  const [_callableObject, setCallableObject] = useState(null);
  useEffect(() => {
    setSelectedFromNumber(defaultFromNumber);
  }, [defaultFromNumber]);
  const openCallTab = () => {
    setPersistedCallMethod(CALL_FROM_BROWSER);
    window.open(callingWindowUrl(PortalIdParser.get() || ''), 'calling-window', `menubar=no,resizable=no,scrollbars=yes,location=no,width=${CALL_WINDOW_WIDTH_WITHOUT_PX},height=${CALL_WINDOW_HEIGHT_WITHOUT_PX}`);
  };
  const createStartCallMessageAndStartCall = useCallback(({
    numberIdentifier,
    callableObject
  }) => {
    if (!callableObject || !numberIdentifier) {
      return;
    }
    const numberPropertyName = getPropertyName(numberIdentifier);
    const phoneNumberProperties = getPhoneNumberProperties(callableObject);
    const propertyToCall = getIn([numberPropertyName], phoneNumberProperties) || null;
    const phoneNumberMetadata = propertyToCall && getMetadata(propertyToCall);
    const toNumber = phoneNumberMetadata.toNumberString;
    const fromNumber = getIn(['phoneNumber'], selectedFromNumber);
    const accountType = getAccountTypeFromPhoneNumber(selectedFromNumber);
    const callableObjectJS = callableObject.toJS();
    const strippedCallableObject = {
      crmObjectSubscriptionStatus: callableObjectJS.crmObjectSubscriptionStatus,
      objectId: callableObjectJS.objectId,
      objectTypeId: callableObjectJS.objectTypeId
    };
    sendCrossTabMessage(createInitiateOutboundCallMessage({
      destinationConnectionId: activeConnectionId,
      callableObject: strippedCallableObject,
      toNumber,
      fromNumber,
      source: source || CallSource.CRM,
      accountType,
      // not needed for redesign
      requireContactOrCompany,
      prospectingQueueItemId,
      threadId: threadId ? threadId.toString() : undefined
    })).catch(error => {
      logCallingError({
        error,
        tags: {
          requestName: 'sendCrossTabMessage - initiate outbound call'
        }
      });
    });
  }, [activeConnectionId, prospectingQueueItemId, selectedFromNumber, source, requireContactOrCompany, threadId]);
  useEffect(() => {
    if (fromNumbers.length > 0 && Boolean(activeConnectionId) && isTryingToStartCall && isDeviceReady(twilioDeviceStatus) && _callableObject && _numberIdentifier) {
      createStartCallMessageAndStartCall({
        callableObject: _callableObject,
        numberIdentifier: _numberIdentifier
      });
      setIsTryingToStartCall(false);
      if (onCallButtonClick) {
        onCallButtonClick();
      }
    }
  }, [_callableObject, _numberIdentifier, activeConnectionId, createStartCallMessageAndStartCall, fromNumbers.length, isTryingToStartCall, onCallButtonClick, twilioDeviceStatus]);
  const startThirdPartyOutboundCall = useCallback(({
    numberIdentifier,
    callableObject
  }) => {
    if (!getIsThirdPartyCallingPopoverInUse(gates, selectedCallProvider) && onLegacyOutboundCallStart) {
      onLegacyOutboundCallStart();
    }
    const phoneNumberProperties = getPhoneNumberProperties(callableObject);
    const numberPropertyName = getPropertyName(numberIdentifier);
    triggerThirdPartyOutboundCall({
      objectId: getObjectId(callableObject),
      objectTypeId: getObjectTypeId(callableObject),
      phoneNumberProperties,
      numberPropertyName,
      gates,
      selectedCallProvider,
      threadId
    });
    if (onCallButtonClick) {
      onCallButtonClick();
    }
  }, [gates, onCallButtonClick, onLegacyOutboundCallStart, selectedCallProvider, threadId]);
  const startOutboundCall = ({
    numberIdentifier,
    callableObject
  }) => {
    if (!getIsTwilioBasedCallProvider(selectedCallProvider)) {
      startThirdPartyOutboundCall({
        numberIdentifier,
        callableObject
      });
      return;
    }
    const fromNumber = getIn(['phoneNumber'], selectedFromNumber);
    const fromNumberSource = getIn(['source'], selectedFromNumber);
    const callProvider = fromNumberSource === PhoneNumberSources.CONNECT_NUMBER ? 'Twilio' : 'Hubspot';
    const isChannelConnectedFromNumber = !!getIn(['workspaceIdAndType'], selectedFromNumber);
    if (fromNumber) {
      CommunicatorLogger.log('callUsage_startCall', {
        action: 'start call',
        callMedium: 'CALLING_PAGE_UI',
        provider: callProvider,
        source: appIdentifier || ACTIVITY_CREATOR,
        phoneNumberType: fromNumberSource,
        isChannelConnectedFromNumber,
        ticketId,
        threadId
      });
      InboundCallingTracker.trackCallTabStartOutboundCall({
        appIdentifier
      });
      if (activeCallTabOpen) {
        createStartCallMessageAndStartCall({
          callableObject,
          numberIdentifier
        });
        if (onCallButtonClick) {
          onCallButtonClick();
        }
      } else {
        setNumberIdentifier(numberIdentifier);
        setCallableObject(callableObject);
        setIsTryingToStartCall(true);
        openCallTab();
      }
    }
  };
  return {
    startOutboundCall
  };
};